import Phaser from 'phaser';

/**
 * Bake a "thin-film" alpha profile into a bubble sprite texture.
 *
 * Real soap bubbles are mostly transparent through the body — the fish, the
 * coral, the diver are visible *through* the film — and read as bubbles only
 * because the curved rim catches a brighter highlight where light refracts off
 * the film at a glancing angle. The very outer edge then feathers softly into
 * the surrounding water.
 *
 * We approximate that with a piecewise radial alpha curve, multiplied into the
 * existing PNG alpha channel:
 *
 *   r ∈ [0, bodyEnd]        → α' = α * bodyAlpha     (translucent body)
 *   r ∈ [bodyEnd, rimPeak]  → α' = α * smoothstep(bodyAlpha → rimAlpha)
 *   r ∈ [rimPeak, edgeEnd]  → α' = α * smoothstep(rimAlpha  → edgeAlpha)
 *
 * Defaults are tuned so background ambient life (fish, coral) reads clearly
 * through the body while the bubble's silhouette + tint remain unmistakable.
 *
 * Done once at boot — every spawned bubble inherits the new profile through
 * texture sharing, so per-frame cost is zero and the GameScene tint / blend
 * paths stay untouched.
 */
export function bakeBubbleEdgeFalloff(
  scene: Phaser.Scene,
  textureKey: string,
  options: {
    bodyAlpha?: number;
    rimAlpha?: number;
    edgeAlpha?: number;
    bodyEnd?: number;
    rimPeak?: number;
  } = {},
): void {
  // True soap-film look: the body is a barely-there wash and almost all the
  // bubble's presence comes from a thin bright rim near the silhouette plus
  // the highlight + chromatic overlays GameScene paints on top. Real soap
  // bubbles only catch light at glancing angles where the curved film bends
  // it — straight-on you mostly see right through them.
  const bodyAlpha = options.bodyAlpha ?? 0.10;
  const rimAlpha = options.rimAlpha ?? 0.92;
  const edgeAlpha = options.edgeAlpha ?? 0.22;
  // Keep body wide and rim thin — most of the radius is "see-through"
  // and the bright ring sits right at the silhouette.
  const bodyEnd = options.bodyEnd ?? 0.65;
  const rimPeak = options.rimPeak ?? 0.90;

  if (!scene.textures.exists(textureKey)) return;

  const srcTex = scene.textures.get(textureKey);
  const srcImg = srcTex.getSourceImage() as HTMLImageElement | HTMLCanvasElement;
  if (!srcImg || !srcImg.width || !srcImg.height) return;

  const w = srcImg.width;
  const h = srcImg.height;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.drawImage(srcImg as CanvasImageSource, 0, 0);

  let imgData: ImageData;
  try {
    imgData = ctx.getImageData(0, 0, w, h);
  } catch {
    return;
  }
  const data = imgData.data;

  const cx = w / 2;
  const cy = h / 2;
  const inscribedR = Math.min(w, h) / 2;

  const smoothstep = (a: number, b: number, t: number): number => {
    if (t <= 0) return a;
    if (t >= 1) return b;
    const s = t * t * (3 - 2 * t);
    return a + s * (b - a);
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x - cx) / inscribedR;
      const dy = (y - cy) / inscribedR;
      const dist = Math.sqrt(dx * dx + dy * dy);

      let alphaMul: number;
      if (dist <= bodyEnd) {
        alphaMul = bodyAlpha;
      } else if (dist <= rimPeak) {
        const t = (dist - bodyEnd) / (rimPeak - bodyEnd);
        alphaMul = smoothstep(bodyAlpha, rimAlpha, t);
      } else if (dist <= 1.0) {
        const t = (dist - rimPeak) / (1.0 - rimPeak);
        alphaMul = smoothstep(rimAlpha, edgeAlpha, t);
      } else {
        alphaMul = edgeAlpha;
      }

      const idx = (y * w + x) * 4 + 3;
      data[idx] = Math.round(data[idx] * alphaMul);
    }
  }

  ctx.putImageData(imgData, 0, 0);

  scene.textures.remove(textureKey);
  scene.textures.addCanvas(textureKey, canvas);
}

/**
 * Apply the falloff to every unique bubble sprite texture loaded in BootScene.
 * Called once after assets finish loading, before MenuScene starts.
 */
export function bakeAllBubbleEdges(scene: Phaser.Scene, keys: string[]): void {
  const seen = new Set<string>();
  for (const key of keys) {
    if (seen.has(key)) continue;
    seen.add(key);
    bakeBubbleEdgeFalloff(scene, key);
  }
}
